import { useState, useEffect, useRef } from 'react'
import { dialog, preview as platformPreview } from '@carrent/platform'
import { useParams, useNavigate } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { ticketsApi } from '@carrent/api'
import { ticketStatusMeta, isTicketClosed } from '@carrent/domain'
import { api } from '@carrent/api'
import { useAuthStore } from '@carrent/core'

const PRIORITY_COLORS = {
  low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444',
}

function MessageBubble({ msg, isMine }) {
  if (msg.is_system) {
    return (
      <div style={{
        textAlign: 'center', padding: '6px 12px', margin: '4px auto',
        fontSize: '0.78rem', color: 'var(--text-muted)',
        background: 'var(--bg-card)', borderRadius: 99,
        maxWidth: '80%', whiteSpace: 'pre-wrap',
      }}>
        ⚙️ {msg.body}
      </div>
    )
  }

  const align = isMine ? 'flex-end' : 'flex-start'
  const bg    = isMine ? 'var(--accent)' : 'var(--bg-card)'
  const color = isMine ? '#fff' : 'var(--text)'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: align,
      gap: 3, maxWidth: '85%', alignSelf: align }}>
      {!isMine && (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 8 }}>
          {msg.is_admin ? '🔧 ' : '🚗 '}{msg.author_name}
        </span>
      )}
      <div style={{
        background: bg, color, padding: msg.body ? '8px 12px' : 4,
        borderRadius: isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        wordBreak: 'break-word', whiteSpace: 'pre-wrap',
        fontSize: '0.9rem',
      }}>
        {msg.body}
        {msg.attachments?.length > 0 && (
          <div style={{ display: 'grid',
            gridTemplateColumns: msg.attachments.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: 4, marginTop: msg.body ? 6 : 0 }}>
            {msg.attachments.map(a => (
              <a key={a.id} href={a.url} target="_blank" rel="noreferrer">
                <img src={a.url} alt=""
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover',
                    borderRadius: 8 }} />
              </a>
            ))}
          </div>
        )}
      </div>
      <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)',
        marginLeft: !isMine ? 8 : 0, marginRight: isMine ? 8 : 0 }}>
        {new Date(msg.created_at).toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })}
      </span>
    </div>
  )
}

function MessageInput({ onSend, disabled }) {
  const [text, setText]   = useState('')
  const [files, setFiles] = useState([])
  const [sending, setSending] = useState(false)
  const fileRef = useRef()

  const send = async () => {
    if (!text.trim() && files.length === 0) return
    setSending(true)
    const fd = new FormData()
    if (text.trim()) fd.append('body', text.trim())
    files.forEach((f, i) => fd.append(`file_${i}`, f))
    try {
      await onSend(fd)
      setText(''); setFiles([])
    } finally { setSending(false) }
  }

  return (
    <div style={{
      borderTop: '1px solid var(--border)',
      padding: 'var(--gap-sm) var(--gap)',
      paddingBottom: 'calc(var(--gap-sm) + env(safe-area-inset-bottom, 0px))',
      background: 'var(--bg)',
    }}>
      {files.length > 0 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 6, overflowX: 'auto' }}>
          {files.map((f, i) => (
            <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
              <img src={platformPreview.objectUrl(f)} alt=""
                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
              <button onClick={() => setFiles(p => p.filter((_, k) => k !== i))}
                style={{
                  position: 'absolute', top: -4, right: -4,
                  width: 18, height: 18, borderRadius: '50%',
                  background: 'var(--danger)', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: '0.7rem',
                }}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <button onClick={() => fileRef.current?.click()} disabled={disabled}
          style={{
            background: 'none', border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', width: 40, height: 40,
            cursor: 'pointer', flexShrink: 0, fontSize: '1.1rem',
          }}>📎</button>
        <input ref={fileRef} type="file" accept="image/*" multiple
          style={{ display: 'none' }}
          onChange={e => { setFiles(p => [...p, ...Array.from(e.target.files)]); e.target.value = '' }} />

        <textarea className="input" rows={1}
          placeholder={disabled ? 'Обращение закрыто' : 'Сообщение...'}
          disabled={disabled}
          value={text} onChange={e => setText(e.target.value)}
          style={{ resize: 'none', flex: 1, minHeight: 40, maxHeight: 120 }} />

        <button onClick={send}
          disabled={disabled || sending || (!text.trim() && files.length === 0)}
          style={{
            background: 'var(--accent)', color: '#fff',
            border: 'none', borderRadius: 'var(--r-sm)',
            width: 40, height: 40, cursor: 'pointer',
            opacity: (!text.trim() && files.length === 0) || disabled ? 0.5 : 1,
            flexShrink: 0, fontSize: '1.1rem',
          }}>
          {sending ? <span className="spinner" style={{ width: 16, height: 16 }} /> : '→'}
        </button>
      </div>
    </div>
  )
}

/**
 * Обращение водителя.
 *
 * Раньше компонент принимал проп isAdmin и содержал обе ветки. Из-за
 * этого в водительский бандл уезжали панель AdminControls, вызов
 * /admin/cars и маршруты /admin/tickets — всё это мёртвый код,
 * поскольку здесь isAdmin всегда false.
 */
export default function TicketChat() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [ticket, setTicket]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')
  const messagesEndRef = useRef()

  const load = async () => {
    try {
      const r = await ticketsApi.get(id)
      setTicket(r.data?.data)
      setError('')
    } catch {
      // Молчаливый catch скрывал от пользователя недоступность тикета:
      // экран оставался пустым без объяснения причины.
      setError('Не удалось загрузить обращение')
    }
    setLoading(false)
  }

  useEffect(() => { load() }, [id])

  // Опроса здесь нет намеренно.
  //
  // Раньше экран держал собственный setInterval поверх такого же
  // в useTicket: два независимых поллера на одну переписку. Это
  // не только лишние запросы, но и гонка — ответы на разные моменты
  // времени перезаписывали состояние друг друга.
  //
  // Обновлением занимается useTicket: реалтайм по событиям,
  // опрос только когда поток недоступен.

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [ticket?.messages?.length])

  const handleSend = async (fd) => {
    await ticketsApi.reply(id, fd)
    await load()
  }

  const handleClose = async () => {
    if (!await dialog.confirm('Закрыть это обращение?')) return
    await ticketsApi.close(id)
    await load()
  }

  if (loading) return (
    <div>
      <PageHeader title="Загрузка..." backTo="/support" />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    </div>
  )
  // Ошибка загрузки и отсутствие тикета — разные состояния,
  // и пользователю нужно видеть разницу.
  if (error) return (
    <div>
      <PageHeader title="Ошибка" backTo="/support" />
      <div className="state">
        <p className="state__title">{error}</p>
        <p className="state__text">Проверьте соединение и попробуйте ещё раз.</p>
        <button type="button" className="btn btn--secondary" onClick={() => { setLoading(true); load() }}>
          Повторить
        </button>
      </div>
    </div>
  )
  if (!ticket) return (
    <div>
      <PageHeader title="Не найдено" backTo="/support" />
      <div className="state">
        <p className="state__title">Обращение не найдено</p>
        <p className="state__text">Возможно, оно было удалено или у вас нет к нему доступа.</p>
      </div>
    </div>
  )

  const isClosed = isTicketClosed(ticket)
  const statusTone = ticketStatusMeta(ticket.status).tone

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh' }}>
      <PageHeader
        title={`#${ticket.id} ${ticket.subject}`}
        backTo="/support"
        right={
          <span className={`badge badge--${statusTone}`}>
            {ticketStatusMeta(ticket.status).label}
          </span>
        }
      />

      {/* Шапка с метаданными */}
      <div style={{ padding: 'var(--gap-sm) var(--gap)',
        background: 'var(--bg-card)', borderBottom: '1px solid var(--border)',
        fontSize: '0.78rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{
            padding: '2px 8px', borderRadius: 99,
            background: `${PRIORITY_COLORS[ticket.priority]}20`,
            color: PRIORITY_COLORS[ticket.priority], fontWeight: 700,
          }}>
            {ticket.priority_label}
          </span>
          <span className="text-muted">{ticket.category_label}</span>
          {ticket.car && (
            <span className="text-muted">
              🚗 {ticket.car.brand} {ticket.car.model} · {ticket.car.plate}
            </span>
          )}
        </div>

      </div>

      <div style={{
        flex: 1, overflowY: 'auto', padding: 'var(--gap)',
        display: 'flex', flexDirection: 'column', gap: 8,
        background: 'var(--bg)',
      }}>
        {ticket.messages?.map(m => (
          <MessageBubble key={m.id} msg={m} isMine={m.author_id === user?.id} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {!isClosed && <MessageInput onSend={handleSend} disabled={isClosed} />}

      {!isClosed && (
        <div style={{ padding: '0 var(--gap) var(--gap-sm)' }}>
          <button onClick={handleClose}
            className="btn btn--outline btn--full"
            style={{ fontSize: '0.85rem' }}>
            🔒 Закрыть обращение
          </button>
        </div>
      )}

      {isClosed && (
        <div style={{ padding: 'var(--gap)', textAlign: 'center',
          background: 'var(--bg-card)', borderTop: '1px solid var(--border)',
          color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          🔒 Обращение закрыто
        </div>
      )}
    </div>
  )
}
